import { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { postSendVerifyEmail, postVerify } from '../../../api/auth';
import { useUserApi } from '../../../api/user';
import { showVerificationPages, secondsToWaitForSendingNewCode } from '../../../constants';
import { delay, maskEmail } from '../../../utils/miscellaneous';

export const useUserDataFetcher = () => {
  const { email, setEmail } = useOutletContext();
  const [ maskedEmail, setMaskedEmail ] = useState( '' );
  const [ phoneNumber, setPhoneNumber ] = useState( '' );
  const [ isInitialDataFetched, setIsInitialDataFetched ] = useState( false );
  const userData = useUserApi();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const userDataReq = await userData.getUserData();

      if ( !showVerificationPages || userDataReq.data.is_email_verified ) {        
        // navigate( '/demographics' );
        navigate( '/edit-or-scan' );
        return;
      }

      setEmail( userDataReq.data.email );
      setPhoneNumber( userDataReq.data.phone_number );
    } catch ( error ) {
      console.error( 'Error fetching user data:', error );
    } finally {
      // Always set initial data as fetched to prevent loading state
      setIsInitialDataFetched( true );
    }
  };

  useEffect( () => {
    if ( !isInitialDataFetched ) {
      fetchData();
    }
  }, [ isInitialDataFetched ] );

  useEffect( () => {
    if ( email ) {
      setMaskedEmail( maskEmail( email ) );
    }
  }, [ email ] );

  return { email, maskedEmail, phoneNumber, isInitialDataFetched };
};

export const useVerificationLogic = ( email, phoneNumber, isInitialDataFetched ) => {
  const navigate = useNavigate();
  const userData = useUserApi();
  const [ code, setCode ] = useState( '' );
  const [ isLoading, setIsLoading ] = useState( false ); // Start with false instead of true
  const [ responseReturned, setResponseReturned ] = useState( false );
  const [ incorrect, setIncorrect ] = useState( false );
  const [ sendNewCodeActivated, setSendNewCodeActivated ] = useState( false );
  const hasInitialCodeBeenSent = useRef( false );
  const isVerifying = useRef( false );

  // Send initial verification code
  useEffect( () => {
    const sendInitialVerificationCode = async () => {
      if ( email && isInitialDataFetched && !hasInitialCodeBeenSent.current && !isVerifying.current ) {
        try {
          setIsLoading( true );
          await postSendVerifyEmail( email );
          hasInitialCodeBeenSent.current = true;
          setResponseReturned( true );
        } catch ( error ) {
          console.error( 'Error sending initial verification code:', error );
          if ( error.response?.status === 401 ) {
            navigate( '/' );
          }
        } finally {
          setIsLoading( false );
        }
      }
    };

    sendInitialVerificationCode();
  }, [ email, isInitialDataFetched ] );

  useEffect( () => {
    delay( secondsToWaitForSendingNewCode ).then( () => {
      setSendNewCodeActivated( true );
    } );
  }, [] );

  const handleSendVerify = async () => {
    if ( isVerifying.current ) return;

    setCode( '' );
    setIncorrect( false );
    setSendNewCodeActivated( false );

    try {
      setIsLoading( true );
      await postSendVerifyEmail( email );
      delay( secondsToWaitForSendingNewCode ).then( () => {
        setSendNewCodeActivated( true );
      } );
    } catch ( error ) {
      if ( error.response?.status === 401 ) {
        navigate( '/' );
      }
    } finally {
      setIsLoading( false );
    }
  };

  const handleVerify = async () => {
    if ( isVerifying.current ) return;
    isVerifying.current = true;

    try {
      setIsLoading( true );
      const verifyingReq = await postVerify( email, code );

      if ( verifyingReq.status === 200 ) {
        await userData.updateMultiFactorAuthenticationUsage( true );
        navigate( phoneNumber === '' ? '/demographics' : '/verify-phone' );
        return;
      }
    } catch ( error ) {
      if ( error.response?.status === 409 ) {
        await userData.updateMultiFactorAuthenticationUsage( true );
        navigate( phoneNumber === '' ? '/demographics' : '/verify-phone' );
        return;
      }
      if ( error.response?.status === 403 ) {
        setIncorrect( true );
      }
      console.error( 'Error during verification:', error );
    } finally {
      setIsLoading( false );
      isVerifying.current = false;
    }
  };

  // If initial data is not fetched, show loading
  const finalIsLoading = !isInitialDataFetched || isLoading;

  return {
    code,
    setCode,
    isLoading: finalIsLoading,
    responseReturned,
    incorrect,
    sendNewCodeActivated,
    handleSendVerify,
    handleVerify,
  };
};